import { ref } from "vue";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { getCurrentUser } from "@/api/auth";

const token = ref(localStorage.getItem("token"));
const isLoggedIn = ref(!!token.value);
const user = ref(null);
const userInfoLoaded = ref(false);
const isOIDCUser = ref(localStorage.getItem("is_oidc_user") === "true");

export function useAuth() {
  function decodeToken(jwt) {
    try {
      const decoded = jwtDecode(jwt);
      user.value = {
        id: decoded.sub,
        user_type: decoded.user_type,
        micro_credential_id: decoded.micro_credential_id,
      };
      isLoggedIn.value = true;
    } catch (err) {
      console.error("Invalid JWT token:", err);
      logout();
    }
  }

  async function fetchUserInfo() {
    // Only fetch if not already loaded
    if (userInfoLoaded.value) return;
    
    try {
      const response = await getCurrentUser();
      user.value = {
        ...user.value,
        ...response.data,
        user_type: response.data.user_type.name,
      };
      userInfoLoaded.value = true;
      
      // Check if user is an OIDC user and store this info
      if (response.data.auth_provider === 'keycloak') {
        isOIDCUser.value = true;
        localStorage.setItem("is_oidc_user", "true");
      }
    } catch (err) {
      console.error("Failed to fetch user info:", err);
    }
  }

  function login(newAccessToken, newRefreshToken, oidcLogin = false) {
    token.value = newAccessToken;
    localStorage.setItem("token", newAccessToken);
    if (newRefreshToken) localStorage.setItem("refresh_token", newRefreshToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
    
    // Track if this is an OIDC login
    if (oidcLogin) {
      isOIDCUser.value = true;
      localStorage.setItem("is_oidc_user", "true");
    }
    
    decodeToken(newAccessToken);
    // Fetch full user info after login
    fetchUserInfo();
  }

  function logout() {
    const wasOIDCUser = isOIDCUser.value;
    
    // Clear local session
    token.value = null;
    isLoggedIn.value = false;
    user.value = null;
    userInfoLoaded.value = false;
    isOIDCUser.value = false;
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("is_oidc_user");
    delete axios.defaults.headers.common["Authorization"];
    
    // Return whether this was an OIDC user (for caller to handle Keycloak logout)
    return wasOIDCUser;
  }

  function loadFromStorage() {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      token.value = storedToken;
      axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
      isOIDCUser.value = localStorage.getItem("is_oidc_user") === "true";
      decodeToken(storedToken);
      // Fetch full user info
      fetchUserInfo();
    }
  }

  return { token, isLoggedIn, user, isOIDCUser, login, logout, loadFromStorage };
}
